<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import SearchAPI from 'dashboard/api/search';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  messageId: { type: Number, required: true },
  conversationId: { type: Number, required: true },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const query = ref('');
const conversations = ref([]);
const selectedIds = ref([]);
const isLoading = ref(false);
const isForwarding = ref(false);

// The search endpoint answers on an empty query too — `q` becomes `%%` and every conversation matches —
// so the list the agent sees before typing is the account's most recent chats, and typing narrows it.
// Forwarding into the conversation the message already lives in would just duplicate it in place, so the
// source is the one chat kept out of the list.
const fetchConversations = async () => {
  isLoading.value = true;
  try {
    const { data } = await SearchAPI.conversations({ q: query.value });
    conversations.value = (data.payload?.conversations ?? []).filter(
      conversation => conversation.id !== props.conversationId
    );
  } catch (error) {
    useAlert(parseAPIErrorResponse(error));
  } finally {
    isLoading.value = false;
  }
};

watch(query, useDebounceFn(fetchConversations, 300));

const isSelected = id => selectedIds.value.includes(id);

const toggle = id => {
  selectedIds.value = isSelected(id)
    ? selectedIds.value.filter(selected => selected !== id)
    : [...selectedIds.value, id];
};

const conversationLabel = conversation => {
  const { contact } = conversation;
  return (
    contact?.name ||
    contact?.phone_number ||
    contact?.email ||
    `#${conversation.id}`
  );
};

const confirmLabel = computed(() =>
  selectedIds.value.length
    ? t('CONVERSATION.FORWARD.CONFIRM_WITH_COUNT', selectedIds.value.length)
    : t('CONVERSATION.FORWARD.CONFIRM')
);

const handleForward = async () => {
  if (!selectedIds.value.length) return;

  isForwarding.value = true;
  try {
    await store.dispatch('forwardMessage', {
      conversationId: props.conversationId,
      messageId: props.messageId,
      conversationIds: selectedIds.value,
    });
    useAlert(
      t('CONVERSATION.FORWARD.SUCCESS', selectedIds.value.length)
    );
    dialogRef.value?.close();
  } catch (error) {
    // The backend refuses a private note, a deleted message and a message with nothing forwardable, and
    // says which — a generic failure would leave the agent guessing.
    useAlert(
      parseAPIErrorResponse(error) || t('CONVERSATION.FORWARD.FAILED')
    );
  } finally {
    isForwarding.value = false;
  }
};

const open = () => {
  query.value = '';
  selectedIds.value = [];
  conversations.value = [];
  dialogRef.value?.open();
  fetchConversations();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    position="top"
    :title="t('CONVERSATION.FORWARD.TITLE')"
    :description="t('CONVERSATION.FORWARD.DESCRIPTION')"
    :confirm-button-label="confirmLabel"
    :disable-confirm-button="!selectedIds.length"
    :is-loading="isForwarding"
    @confirm="handleForward"
  >
    <div class="flex flex-col gap-3">
      <Input
        v-model="query"
        autofocus
        :placeholder="t('CONVERSATION.FORWARD.SEARCH_PLACEHOLDER')"
      />
      <div class="flex flex-col h-64 gap-1 overflow-y-auto">
        <div v-if="isLoading" class="flex justify-center py-6">
          <Spinner />
        </div>
        <p
          v-else-if="!conversations.length"
          class="py-6 mb-0 text-sm text-center text-n-slate-11"
        >
          {{ t('CONVERSATION.FORWARD.EMPTY') }}
        </p>
        <template v-else>
          <button
            v-for="conversation in conversations"
            :key="conversation.id"
            type="button"
            class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-n-alpha-1"
            @click="toggle(conversation.id)"
          >
            <Checkbox :model-value="isSelected(conversation.id)" />
            <Avatar :name="conversationLabel(conversation)" :size="24" />
            <div class="flex flex-col min-w-0 text-start">
              <span class="text-sm truncate text-n-slate-12">
                {{ conversationLabel(conversation) }}
              </span>
              <span class="text-xs truncate text-n-slate-11">
                {{ conversation.inbox?.name }}
              </span>
            </div>
          </button>
        </template>
      </div>
    </div>
  </Dialog>
</template>
